import {
  type ComponentOptions,
  type defineComponent,
  isReactive,
  readonly,
  ref,
  shallowReactive,
} from 'vue';
import {
  type NavigationGuardNext,
  type NavigationGuardNextCallback,
  type NavigationGuardWithThis,
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric,
  type RouteLocationRaw,
  type RouteMeta,
  type Router,
  type RouteRecordRaw,
  useRoute,
} from 'vue-router';

import type { NonVoidOrStrictVoid } from '@scaffold/common';

declare module 'vue-router' {
  interface RouteMeta {
    layout?: ComponentOptions;
    layoutProps?: { [key: string]: unknown };
    error?: unknown;
    noHydrate?: boolean;
  }
}

export class ClientOnlyError extends Error {
  readonly name = 'ClientOnlyError';
}

export class ServerOnlyError extends Error {
  readonly name = 'ServerOnlyError';
}

export type RouteContext<T> = T & {
  router: Router;
  to: RouteLocationNormalizedGeneric;
  from: RouteLocationNormalizedLoadedGeneric;
};

export type RouteMiddleware<T> = (
  context: RouteContext<T>,
) => Promise<NavigationGuardNextCallback | RouteLocationRaw | boolean | void>;

export type RouteModule = {
  default: ReturnType<typeof defineComponent>;
  defineRouteLayout?: {
    payload?: {
      layout: RouteLoader | undefined;
      props: { [key: string]: unknown };
    };
  };
  defineRouteMiddleware?: {
    payload?: RouteMiddleware<unknown> | RouteMiddleware<unknown>[];
  };
  defineRouteMeta?: {
    payload?: (context: RouteContext<unknown>) => unknown;
  };
};

type RouteLoader = () => Promise<RouteModule>;

export function useRouteSetError() {
  const route = useRoute();

  return (err: unknown) => {
    if (import.meta.env.DEV) console.log({ err });

    route.meta.error = err;
  };
}

export function exportRoute() {
  const defineRouteMeta = <T, U extends RouteMeta | void>(
    fetch: (context: RouteContext<T>) => Promise<U>,
  ) => {
    (
      defineRouteMeta as { payload?: (context: RouteContext<T>) => Promise<U> }
    ).payload = fetch;

    return {
      useRouteMeta() {
        const route = useRoute();
        return route.meta as NonVoidOrStrictVoid<U> & RouteMeta;
      },
    };
  };

  const defineRouteMiddleware = <T>(
    middleware: RouteMiddleware<T> | RouteMiddleware<T>[],
  ) => {
    (
      defineRouteMiddleware as {
        payload?: RouteMiddleware<T> | RouteMiddleware<T>[];
      }
    ).payload = middleware;
  };

  const defineRouteLayout = (
    layout: RouteLoader | undefined,
    props?: { [key: string]: unknown },
  ) => {
    (
      defineRouteLayout as {
        payload?: {
          layout: RouteLoader | undefined;
          props?: { [key: string]: unknown };
        };
      }
    ).payload = {
      layout,
      props,
    };
  };

  return {
    defineRouteMeta,
    defineRouteMiddleware,
    defineRouteLayout,
  };
}

export function exportServerOnlyRoute<T extends Record<string, unknown>>(
  getter: () => Promise<T | { default: T }>,
) {
  const { defineRouteMeta } = exportRoute();

  const { useRouteMeta } = defineRouteMeta(async () => {
    if (import.meta.env.SSR || import.meta.env.DEV) {
      const data = await getter();
      if ('default' in data) return data.default as T;

      return data;
    }

    throw new ServerOnlyError();
  });

  return { defineRouteMeta, useRouteMeta };
}

type RouteRecordRawWithModule = RouteRecordRaw & {
  module?: RouteLoader;
};

export const createModuleRoutes = (
  routeModules: Partial<Record<string, RouteLoader>>,
  matcher: RegExp,
) =>
  Object.keys(routeModules).reduce((routes, modulePath) => {
    const rawPath = modulePath.match(matcher)?.[1];
    if (rawPath !== undefined) {
      // https://router.vuejs.org/guide/essentials/route-matching-syntax.html#Optional-parameters
      const path =
        rawPath
          .toLowerCase()
          .replaceAll('/_', '/:')
          .replaceAll(/(:[^/]+)_/g, '$1?') || '/';
      const module = routeModules[modulePath]!;
      routes.push({
        path,
        name: path === '/' ? 'index' : path.slice(1).replaceAll(/[/:?]+/g, '-'),
        module,
        component: async () => (await module()).default,
      });
    }
    return routes;
  }, [] as RouteRecordRawWithModule[]);

export const onRouterError = <T>(
  { router, from, to }: RouteContext<T>,
  err: unknown,
  next: NavigationGuardNext,
) => {
  if (err instanceof ServerOnlyError) {
    if (from.name && from.path !== to.path) {
      location.href = router.resolve(to).href;

      next(false);
      return;
    }

    next();
    return;
  }

  to.meta.error = err instanceof Error ? err : { payload: err };
  next();
};

export const createModuleGuard = <T>({
  context,
  router,
  routes,
  defaultLayout,
  onError = onRouterError,
}: {
  context: T;
  router: Router;
  routes: RouteRecordRawWithModule[];
  defaultLayout?: RouteLoader;
  onError: (
    context: RouteContext<T>,
    err: unknown,
    next: NavigationGuardNext,
  ) => void;
}) =>
  async function (to, from, next) {
    const route = routes.find((route) => route.path === to.matched[0].path);
    const routeModule = await route?.module?.();
    if (!routeModule) {
      next();
      return;
    }

    const routeContext = { ...context, router, to, from };

    if (!isReactive(to.meta)) {
      to.meta = shallowReactive(to.meta);
    }

    try {
      if (!('layout' in to.meta)) {
        const routeLayout = routeModule.defineRouteLayout?.payload;
        to.meta.layout =
          routeLayout && 'layout' in routeLayout
            ? routeLayout.layout && (await routeLayout.layout()).default
            : defaultLayout && (await defaultLayout()).default;
        to.meta.layoutProps = routeLayout?.props;
      }

      const routeMiddleware = routeModule.defineRouteMiddleware?.payload;
      if (routeMiddleware) {
        for (const middleware of Array.isArray(routeMiddleware)
          ? routeMiddleware
          : [routeMiddleware]) {
          const result = await middleware(routeContext);
          if (result !== undefined) {
            if (typeof result === 'boolean') next(result);
            else if (typeof result === 'function') next(result);
            else next(result);

            return;
          }
        }
      }

      const fetchRouteMeta = routeModule.defineRouteMeta?.payload;
      if (fetchRouteMeta) {
        Object.assign(to.meta, await fetchRouteMeta(routeContext));
      }
    } catch (err) {
      onError(routeContext, err, next);
      return;
    }

    next();
  } as NavigationGuardWithThis<undefined>;

const routeLoadingRef = ref(false);
export const isRouteLoading = readonly(routeLoadingRef);

export function attachRouteLoading(router: Router) {
  const detaches = [
    router.beforeEach((_to, _from, next) => {
      routeLoadingRef.value = true;
      next();
    }),

    router.afterEach(() => {
      routeLoadingRef.value = false;
    }),

    router.onError(() => {
      routeLoadingRef.value = false;
    }),
  ];

  return () => {
    routeLoadingRef.value = false;
    detaches.forEach((detach) => detach());
  };
}
